import React, { useEffect, useState, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { io } from "socket.io-client";
import { Page, Hero, Panel, Kpi, PButton, C, S } from "../theme/pbi";
import { apiTicketingOverview, dashboardApiBaseUrl } from "../api";

const fmtUGX = (n) => "UGX " + Math.round(Number(n) || 0).toLocaleString();
const fmtInt = (n) => (Number(n) || 0).toLocaleString();
const shortDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch (e) { return "—"; } };
const dateTime = (d) => { try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch (e) { return "—"; } };

// Dependency-free bar chart (tickets issued per day).
const BarChart = ({ data, color }) => {
  if (!data || !data.length) return null;
  const max = Math.max(1, ...data.map((d) => d.tickets));
  const W = 720, H = 140, pad = 2;
  const bw = (W - pad * 2) / data.length;
  return (
    <svg viewBox={"0 0 " + W + " " + H} preserveAspectRatio="none" style={{ width: "100%", height: 140, display: "block" }}>
      {data.map((d, i) => {
        const h = (d.tickets / max) * (H - 16);
        const x = pad + i * bw;
        const y = H - 8 - h;
        return (
          <rect key={d.day} x={x + 1} y={y} width={Math.max(1, bw - 2)} height={Math.max(0, h)} rx={1.5}
            fill={color} opacity={d.tickets ? 0.9 : 0.14}>
            <title>{shortDate(d.day) + " · " + fmtInt(d.tickets) + " tickets · " + fmtUGX(d.gross)}</title>
          </rect>
        );
      })}
    </svg>
  );
};

const FillBar = ({ sold, total }) => {
  const pct = total > 0 ? Math.min(100, Math.round((sold / total) * 100)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: "#eef1f5", borderRadius: 4, overflow: "hidden", minWidth: 54 }}>
        <div style={{ width: pct + "%", height: "100%", background: pct >= 80 ? C.accent : C.green }} />
      </div>
      <span style={{ fontSize: 11, color: C.textLight, minWidth: 84, textAlign: "right" }}>
        {fmtInt(sold)}{total ? " / " + fmtInt(total) : ""}{total ? " · " + pct + "%" : ""}
      </span>
    </div>
  );
};

const TicketingDashboard = () => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async (isAutoRetry) => {
    setLoading(true);
    try {
      const { data } = await apiTicketingOverview();
      setData(data); setErr(""); setLoading(false);
    } catch (e) {
      // A network-level failure (no response object) usually means the API was
      // momentarily unreachable — e.g. the server was restarting. Retry once,
      // staying in the loading state, before surfacing an error to the user.
      if (!isAutoRetry && !(e && e.response)) {
        setTimeout(() => load(true), 1500);
        return;
      }
      setErr((e && e.response && e.response.data && e.response.data.message) || "Could not load the dashboard.");
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // ── realtime: refresh KPIs when any ticketing change is broadcast ──────────
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    let t = null;
    const socket = io(dashboardApiBaseUrl() || undefined, { transports: ["websocket", "polling"], withCredentials: true });
    socket.on("connect", () => socket.emit("join", "ticketing"));
    const onUpdate = () => { clearTimeout(t); t = setTimeout(() => loadRef.current(true), 350); };
    socket.on("ticketing:update", onUpdate);
    return () => { clearTimeout(t); socket.off("ticketing:update", onUpdate); socket.disconnect(); };
  }, []);

  const k = (data && data.kpis) || {};
  const scope = (data && data.scope) || {};
  const isSuper = !!scope.isSuper;
  const subtitle = loading
    ? "Loading…"
    : isSuper
      ? "Platform-wide — " + fmtInt(scope.organizerCount) + " organizer" + (scope.organizerCount === 1 ? "" : "s")
      : (scope.organizerNames && scope.organizerNames.length ? scope.organizerNames.join(", ") : "Your events at a glance");

  return (
    <Page>
      <Hero
        title="Ticketing — Dashboard"
        subtitle={subtitle}
        right={<PButton variant="outline" onClick={() => history.push("/admin/ticketing/console")}>Manage events →</PButton>}
      />

      {err && (
        <Panel title="Couldn't load" color={C.accent}>
          <div style={{ color: C.text, fontSize: 13 }}>
            {err}
            <button onClick={() => load()} style={{ marginLeft: 8, background: "none", border: "none", color: C.blue, cursor: "pointer", fontWeight: 700 }}>Retry</button>
          </div>
        </Panel>
      )}

      {!err && (
        <>
          <Row>
            <Col md={4} lg={2}><Kpi label="Units / admissions" value={loading ? "…" : (fmtInt(k.ticketsSold) + " / " + fmtInt(k.admissionsSold ?? k.ticketsSold))} color={C.green} /></Col>
            <Col md={4} lg={2}><Kpi label="Gross revenue" value={loading ? "…" : fmtUGX(k.gross)} color={C.navy || C.blue} /></Col>
            <Col md={4} lg={2}><Kpi label={isSuper ? "Platform commission" : "Your net"} value={loading ? "…" : fmtUGX(isSuper ? k.platformCommission : k.organizerNet)} color={C.accent} /></Col>
            <Col md={4} lg={2}><Kpi label="Guests checked in" value={loading ? "…" : (fmtInt(k.admissionsRedeemed ?? k.redeemed) + " · " + (k.redeemRate || 0) + "%")} color={C.blue} /></Col>
            <Col md={4} lg={2}><Kpi label="Upcoming showings" value={loading ? "…" : fmtInt(k.upcomingShowings)} color={C.orange} /></Col>
            <Col md={4} lg={2}><Kpi label="Avg seat fill" value={loading ? "…" : ((k.avgFillRate || 0) + "%")} color={C.green} /></Col>
          </Row>

          <Panel title="Sales — last 30 days" sub="tickets issued per day" color={C.blue}>
            {data && data.salesByDay && data.salesByDay.length ? (
              <>
                <BarChart data={data.salesByDay} color={C.blue} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textLight, marginTop: 4 }}>
                  <span>{shortDate(data.salesByDay[0].day)}</span>
                  <span>{shortDate(data.salesByDay[Math.floor(data.salesByDay.length / 2)].day)}</span>
                  <span>Today</span>
                </div>
              </>
            ) : (
              <div style={{ color: C.textLight, fontSize: 13, padding: "8px 0" }}>{loading ? "Loading…" : "No sales in this window yet."}</div>
            )}
          </Panel>

          <Row>
            <Col md={6}>
              <Panel title="Upcoming showings" sub="next on sale" color={C.orange}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={S.th}>When</th><th style={S.th}>Event</th><th style={S.th}>Fill</th></tr></thead>
                  <tbody>
                    {(!data || !data.upcoming || !data.upcoming.length) && <tr><td style={{ ...S.td, color: C.textLight }} colSpan={3}>{loading ? "Loading…" : "No upcoming showings scheduled."}</td></tr>}
                    {data && data.upcoming && data.upcoming.map((s) => (
                      <tr key={s.showingId}>
                        <td style={S.td}><div style={{ fontWeight: 600 }}>{dateTime(s.startsAt)}</div><div style={{ fontSize: 11, color: C.textLight }}>{s.venueName}</div></td>
                        <td style={S.td}>{s.eventTitle}</td>
                        <td style={{ ...S.td, minWidth: 150 }}><FillBar sold={s.seatsSold} total={s.seatsTotal} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </Col>
            <Col md={6}>
              <Panel title="Top events" sub="by sellable units and admissions" color={C.green}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={S.th}>Event</th><th style={{ ...S.th, textAlign: "right" }}>Units</th><th style={{ ...S.th, textAlign: "right" }}>Admissions</th><th style={{ ...S.th, textAlign: "right" }}>Gross</th></tr></thead>
                  <tbody>
                    {(!data || !data.topEvents || !data.topEvents.length) && <tr><td style={{ ...S.td, color: C.textLight }} colSpan={4}>{loading ? "Loading…" : "No tickets sold yet."}</td></tr>}
                    {data && data.topEvents && data.topEvents.map((e) => (
                      <tr key={e.eventId}>
                        <td style={{ ...S.td, fontWeight: 600 }}>{e.title}</td>
                        <td style={{ ...S.td, textAlign: "right" }}>{fmtInt(e.tickets)}</td>
                        <td style={{ ...S.td, textAlign: "right" }}>{fmtInt(e.admissions ?? e.tickets)}</td>
                        <td style={{ ...S.td, textAlign: "right" }}>{fmtUGX(e.gross)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </Col>
          </Row>

          <Panel title="Recent sales" sub="latest issued tickets" color={C.navy || C.blue}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={S.th}>When</th><th style={S.th}>Event</th><th style={S.th}>Ticket / package</th><th style={S.th}>Reserved unit</th><th style={{ ...S.th, textAlign: "right" }}>Admits</th><th style={{ ...S.th, textAlign: "right" }}>Price</th></tr></thead>
              <tbody>
                {(!data || !data.recentSales || !data.recentSales.length) && <tr><td style={{ ...S.td, color: C.textLight }} colSpan={6}>{loading ? "Loading…" : "No sales yet — publish a showing to open sales."}</td></tr>}
                {data && data.recentSales && data.recentSales.map((t, i) => (
                  <tr key={i}>
                    <td style={S.td}>{dateTime(t.createdAt)}</td>
                    <td style={S.td}>{t.eventTitle}</td>
                    <td style={S.td}>{t.ticketTypeName}</td>
                    <td style={S.td}>{t.seatLabel || "GA"}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{fmtInt(t.admissionsCount || 1)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{fmtUGX(t.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </Page>
  );
};

export default TicketingDashboard;
